import { create } from 'zustand';

interface AppStore {
  sidebarCollapsed: boolean;
  bottomPanelCollapsed: boolean;
  sidebarWidth: number;
  bottomPanelHeight: number;
  theme: 'dark' | 'light';
  treeExpanded: Record<string, boolean>;

  // Tab pinning & groups
  tabPins: Record<string, boolean>;
  tabGroups: Record<string, string>;

  /**
   * User overrides for which databases appear in the sidebar tree, per
   * connection. Key = connectionId, nested key = database name, value =
   * explicit show/hide. Missing entries fall back to the default (system
   * databases hidden, everything else visible). This is how the
   * "select databases" affordance on the Connection info tab persists.
   */
  databaseVisibility: Record<string, Record<string, boolean>>;

  /**
   * Per-(connection, database) schema visibility override.
   * `schemaVisibility[connectionId][database][schema] = boolean`.
   * Meaningful primarily for Oracle, where a single DB exposes many
   * schemas (one per user). Without overrides we hide non-default
   * schemas; users opt them in via the database info tab.
   */
  schemaVisibility: Record<string, Record<string, Record<string, boolean>>>;

  toggleSidebar: () => void;
  toggleBottomPanel: () => void;
  setSidebarWidth: (w: number) => void;
  setBottomPanelHeight: (h: number) => void;
  setTheme: (theme: 'dark' | 'light') => void;
  toggleTheme: () => void;
  setTreeExpanded: (expanded: Record<string, boolean>) => void;
  toggleTreeNode: (key: string) => void;

  // Tab pinning & groups
  togglePinTab: (tabId: string) => void;
  setTabGroup: (tabId: string, group: string | null) => void;

  // Database visibility
  setDatabaseVisible: (connectionId: string, dbName: string, visible: boolean) => void;
  clearDatabaseVisibility: (connectionId: string, dbName: string) => void;

  // Schema visibility (per connection, per database)
  setSchemaVisible: (connectionId: string, dbName: string, schemaName: string, visible: boolean) => void;
  clearSchemaVisibility: (connectionId: string, dbName: string, schemaName: string) => void;

  // Hydrate from server-persisted state
  hydrate: (state: Partial<AppStore>) => void;
}

export const useAppStore = create<AppStore>((set) => ({
  sidebarCollapsed: false,
  bottomPanelCollapsed: false,
  sidebarWidth: 264,
  bottomPanelHeight: 180,
  theme: 'dark' as 'dark' | 'light',
  treeExpanded: {},
  tabPins: {},
  tabGroups: {},
  databaseVisibility: {},

  toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
  toggleBottomPanel: () => set((s) => ({ bottomPanelCollapsed: !s.bottomPanelCollapsed })),
  setSidebarWidth: (w) => set({ sidebarWidth: w }),
  setBottomPanelHeight: (h) => set({ bottomPanelHeight: h }),
  setTheme: (theme) => set({ theme }),
  toggleTheme: () => set((s) => ({ theme: s.theme === 'dark' ? 'light' : 'dark' })),
  setTreeExpanded: (expanded) => set({ treeExpanded: expanded }),
  toggleTreeNode: (key) => set((s) => ({
    treeExpanded: { ...s.treeExpanded, [key]: !s.treeExpanded[key] },
  })),

  togglePinTab: (tabId) => set((s) => {
    if (s.tabPins[tabId]) {
      const { [tabId]: _, ...rest } = s.tabPins;
      return { tabPins: rest };
    }
    return { tabPins: { ...s.tabPins, [tabId]: true } };
  }),

  setTabGroup: (tabId, group) => set((s) => {
    if (!group) {
      const { [tabId]: _, ...rest } = s.tabGroups;
      return { tabGroups: rest };
    }
    return { tabGroups: { ...s.tabGroups, [tabId]: group } };
  }),

  setDatabaseVisible: (connectionId, dbName, visible) =>
    set((s) => ({
      databaseVisibility: {
        ...s.databaseVisibility,
        [connectionId]: {
          ...(s.databaseVisibility[connectionId] ?? {}),
          [dbName]: visible,
        },
      },
    })),

  clearDatabaseVisibility: (connectionId, dbName) =>
    set((s) => {
      const current = s.databaseVisibility[connectionId];
      if (!current || !(dbName in current)) return s;
      const { [dbName]: _, ...rest } = current;
      return {
        databaseVisibility: {
          ...s.databaseVisibility,
          [connectionId]: rest,
        },
      };
    }),

  schemaVisibility: {},

  setSchemaVisible: (connectionId, dbName, schemaName, visible) =>
    set((s) => ({
      schemaVisibility: {
        ...s.schemaVisibility,
        [connectionId]: {
          ...(s.schemaVisibility[connectionId] ?? {}),
          [dbName]: {
            ...(s.schemaVisibility[connectionId]?.[dbName] ?? {}),
            [schemaName]: visible,
          },
        },
      },
    })),

  clearSchemaVisibility: (connectionId, dbName, schemaName) =>
    set((s) => {
      const dbMap = s.schemaVisibility[connectionId]?.[dbName];
      if (!dbMap || !(schemaName in dbMap)) return s;
      const { [schemaName]: _, ...rest } = dbMap;
      return {
        schemaVisibility: {
          ...s.schemaVisibility,
          [connectionId]: {
            ...(s.schemaVisibility[connectionId] ?? {}),
            [dbName]: rest,
          },
        },
      };
    }),

  hydrate: (state) => set(state),
}));
